import { useEffect, useState } from "react";
import {
  DataGrid as MuiDataGrid,
  DataGridProps as MuiDataGridProps,
  GridCellParams,
  GridColumnHeaderParams,
} from "@mui/x-data-grid";
import { useAppDispatch } from "../../Redux/store";
import { setDataGridSelection } from "../../Redux/Slices/Datagrid";
import DataGridToolbar, {
  DataGridToolbarAction,
} from "./DatagridLayout/Toolbar/DataGridToolbar";
import DataGridFooter from "./DatagridLayout/Footer/DataGridFooter";
import DataGridFooterPagination from "./DatagridLayout/Footer/DataGridFooterPagination";
import DataGridFooterSelection from "./DatagridLayout/Footer/DataGridFooterSelection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { t } from "i18next";

export type DataGridRow = { id: string | number; [key: string]: unknown };

export type DataGridProps = MuiDataGridProps & {
  actions?: Array<DataGridToolbarAction>;
  onSelectionChanged?: (selection: Array<string | number>) => void;
  data?: Array<string | number>;
  onRowClick?: (params: DataGridRow & { field: string }) => void;
  onPageChange?: (page: number) => void;
  filtered?: boolean;
  disableHeaderCells?: string[];
};

interface RowClickParams extends GridCellParams {
  id: string | number;
}

function DataGrid({
  actions,
  onSelectionChanged = () => { },
  data = [],
  onRowClick,
  onPageChange,
  filtered,
  disableHeaderCells,
  ...props
}: DataGridProps) {
  const dispatch = useAppDispatch();
  const [selectionModel, setSelectionModel] = useState<Array<string | number>>([]);
  const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 25;

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleResize = () => {
    setWindowSize(window.innerWidth);
  };

  const handleSelectAll = (selectAllItems: boolean) => {
    if (selectAllItems === true) {
      const newSelection = data;
      setSelectionModel(newSelection);
      dispatch(setDataGridSelection(newSelection));
      return;
    }

    setSelectionModel([]);
    dispatch(setDataGridSelection([]));
  };

  const handleRowClick = (params: RowClickParams) => {
    // Allow users to select text without the onRowClick event triggering
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return false;

    if (params.field === "__check__" || selectionModel.length > 0) {
      const selectedIndex = selectionModel.indexOf(params.id);
      let newSelection: Array<string | number> = [...selectionModel];

      if (selectedIndex === -1) {
        newSelection.push(params.id);
      } else {
        newSelection.splice(selectedIndex, 1);
      }

      setSelectionModel(newSelection);
      dispatch(setDataGridSelection(newSelection));
      return;
    }

    if (onRowClick) {
      onRowClick({
        ...params,
        id: params.id as string | number,
      } as DataGridRow & { field: string });
    }

    return false;
  };

  const handleHeaderClick = (params: GridColumnHeaderParams) => {
    if (params.field !== "__check__") return;

    let newSelection: Array<string | number> = [...selectionModel];

    const allSelected = props.rows?.every((row) =>
      selectionModel.includes(row.id)
    ) ?? false;

    if (allSelected && props.rows) {
      newSelection = newSelection.filter(
        (id) => !props.rows.some((row) => row.id === id)
      );
    } else if (props.rows) {
      const newIds = props.rows
        .map((row) => row.id)
        .filter((id) => !newSelection.includes(id));
      newSelection.push(...newIds);
    }

    setSelectionModel(newSelection);
    dispatch(setDataGridSelection(newSelection));
  };

  const handlePreviousPage = () => {
    if (currentPage <= 0) {
      return;
    }
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(
      (data.length || props.rows?.length || 0) / pageSize
    );

    if (currentPage >= totalPages - 1) {
      return;
    }

    const newPage = currentPage + 1;
    setCurrentPage(newPage);

    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const UpIcon = () => <FontAwesomeIcon icon={faArrowUp} className="text-gray-500 mx-[2px] text-[16px]" />;
  const DownIcon = () => <FontAwesomeIcon icon={faArrowDown} className="text-gray-500 mx-[2px] text-[16px]" />;

  return (
    <MuiDataGrid
      key={windowSize}
      pageSize={pageSize}
      rowsPerPageOptions={[pageSize]}
      page={currentPage}
      getRowClassName={() => selectionModel.length > 0 ? "Mui-highlight-checkbox" : ""}
      disableColumnMenu
      localeText={{
        columnHeaderSortIconLabel: t("sort") as string,
      }}
      components={{
        ColumnSortedAscendingIcon: UpIcon,
        ColumnSortedDescendingIcon: DownIcon,
        Toolbar: () => (
          <DataGridToolbar
            actions={actions}
            data={Array.isArray(data) ? data.length : 0}
            select={selectionModel.length}
            onSelectAll={handleSelectAll}
          />
        ),
        Footer: () => (
          <DataGridFooter>
            <DataGridFooterPagination
              handleNextPage={handleNextPage}
              handlePreviousPage={handlePreviousPage}
              data={data}
              rows={props.rows ? [...props.rows] as string[] : []}
              currentPage={currentPage}
              pageSize={pageSize}
              loading={props.loading || false}
              filtered={filtered}
            />
            <DataGridFooterSelection
              SelectionModel={selectionModel}
            />
          </DataGridFooter>
        ),
      }}
      onCellClick={handleRowClick}
      selectionModel={selectionModel}
      onColumnHeaderClick={handleHeaderClick}
      onPageChange={(newPage) => {
        setCurrentPage(newPage);
        if (onPageChange) {
          onPageChange(newPage);
        }
      }}
      {...props}
      sx={{
        '& .MuiDataGrid-row:hover.Mui-highlight-checkbox .MuiDataGrid-cell[data-field="__check__"] .MuiDataGrid-checkboxInput:not(:has(input:checked)) .MuiSvgIcon-root': {
          backgroundColor: '#2563eb20',
          borderColor: '#2563eb',
        },
        '& .MuiDataGrid-cell[data-field="__check__"]:hover .MuiDataGrid-checkboxInput:not(:has(input:checked)) .MuiSvgIcon-root': {
          backgroundColor: '#2563eb20',
          borderColor: '#2563eb',
        },
        '& .MuiDataGrid-row': {
          cursor: onRowClick || selectionModel.length > 0 ? "pointer" : "default",
        },
        '& .MuiDataGrid-cell[data-field="actions"]': {
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          padding: "0 8px 0 10px",
        },
        '& .MuiDataGrid-cell[data-field="__check__"]': {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        [`& .MuiDataGrid-columnHeader[data-field="actions"]${disableHeaderCells?.length
          ? disableHeaderCells
            .map(field => `, & .MuiDataGrid-columnHeader[data-field="${field}"]`)
            .join("")
          : ""
          }`]: {
          pointerEvents: "none",
        },
      }}
    />
  );
}

export default DataGrid;
